import React from "react";
import { State } from "state";
import { connect, ConnectedProps } from "react-redux";
import { createAnonymousRequest, hideUserCreationModal } from "state/request";
import {
  Button,
  Grid,
  Modal,
  Paper,
  Typography,
  withStyles,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { buttonStyles } from "styles/buttonStyles";
import Constants from "helpers/constants";
import { SeverityLevel, ToastService } from "services/toastService";

const StyledTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: "#222",
    color: "#fff",
    fontSize: "0.875rem",
    borderRadius: 8,
    padding: "8px 12px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
  },
  arrow: {
    color: "#222",
  },
}))(Tooltip as any);

const styles = (theme: any) => ({
  ...(buttonStyles as any),
  modalPaper: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%", // xs default
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    borderRadius: 12,
    outline: "none",
    backgroundColor: "#fff",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",

    [theme.breakpoints.up("sm")]: {
      width: "80%",
    },
    [theme.breakpoints.up("md")]: {
      width: "65%",
    },
    [theme.breakpoints.up("xl")]: {
      width: "50%",
    },
  },
});

type Props = PropsFromRedux & { classes: any };

type LocalState = {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  company: string;
  chain: string;
  chainName: string;
  stores: string;
};

class UserCreationRequestModal extends React.Component<Props, LocalState> {
  private initialState: LocalState = {
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    company: "",
    chain: "",
    chainName: "",
    stores: "",
  };
  constructor(props: Props) {
    super(props);
  }
  state: LocalState = this.initialState;
  componentDidUpdate(prevProps: Props) {
    if (prevProps.showModal && !this.props.showModal) {
      this.setState(this.initialState);
    }
  }
  handleInputChange =
    (field: string) => (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      this.setState({ [field]: e.target.value } as any);
    };

  private isValidEmail(email: string) {
    // basic email validation
    if (!email) return false;
    const re =
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\\.,;:\s@\"]+\.)+[^<>()[\]\\.,;:\s@\"]{2,})$/i;
    return re.test(email);
  }

  handleSubmit = async () => {
    const { firstname, lastname, email, phone, company, chain, chainName, stores } = this.state;
    const payload = {
      requestType: Constants.USER_ONBOARDING_AND_CHAIN_ACCESS_BY_ANONYMOUS,
      anonymous:true,
      context: {
        initiator_email: email,
        request_intiated_from: "LOGIN PAGE",
        payload_from: "REQUESTOR",
      },
      data: {
        [Constants.USER_ONBOARDING_AND_CHAIN_ACCESS_BY_ANONYMOUS]: [{
          user_email: email,
          phone_no: phone,
          company_name: company,
          first_name: firstname,
          last_name: lastname,
          chain_description: chain,
          chain_name: chainName,
          stores: stores,
        }]
      },
    };
    try {
      const data = await this.props.createAnonymousRequest(payload);
      ToastService.getInstance().publishToast(
        {
          identifier: data,
          title: "Your request has been submitted successfully!",
          message: `Your request ID is ${data}. Our team will review your request and get back to you shortly.`,
        },
        SeverityLevel.SUCCESS,
        { autoClose: false }
      );

      // Close modal
      this.props.hideUserCreationModal();
    } catch (error) {
      console.error("Error creating anonymous request:", error);
    }
    this.props.hideUserCreationModal();
  };

  render() {
    const { firstname, lastname, email, phone, company, chain, chainName, stores } = this.state;
    const {
      showModal,
      hideUserCreationModal,
      classes,
      request: { loading, error, requestId },
    } = this.props;

    // chainName is required, stores is optional
    const hasValue =
      [firstname, lastname, email, phone, company, chain, chainName].every(
        (v) => typeof v === "string" && v.trim().length > 0
      ) && this.isValidEmail(email);
    const isValid = hasValue && !loading;

    const submitStyle: React.CSSProperties = {
      backgroundColor: isValid ? "#D79A00" : "#EDEDED",
      color: isValid ? "#000" : "#9E9E9E",
      borderRadius: 6,
      textTransform: "none",
      padding: "8px 18px",
      cursor: isValid ? "pointer" : "not-allowed",
      boxShadow: isValid ? undefined : "none",
    };

    return (
      <Modal
        open={showModal}
        onClose={() => hideUserCreationModal()}
        closeAfterTransition
        disableBackdropClick
        BackdropProps={{ timeout: 300 }}
      >
        <Paper className={classes.modalPaper}>
          <div
            style={{
              borderBottom: `1px solid #e9eef2`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ padding: "16px 32px" }}>
              <Typography
                variant="subtitle1"
                style={{ fontWeight: 700, fontSize: 20, textTransform: "none", color: "#333333" }}
              >
                Set up your account
              </Typography>
              {/* <Typography variant="body2" style={{ color: "#6b7174" }}>
                Select a user to manage their permissions and access rights.
              </Typography> */}
            </div>
            <div style={{ padding: 12 }}>
              <IconButton
                onClick={() => hideUserCreationModal()}
                aria-label="close"
                disabled={loading}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <div style={{ padding: "16px 32px", overflowY: "auto" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  First Name <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Please enter your first name."
                  fullWidth
                  size="small"
                  required
                  value={this.state.firstname}
                  onChange={this.handleInputChange("firstname")}
                  InputProps={{ style: { borderRadius: 16 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  Last Name <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Please enter your last name."
                  fullWidth
                  size="small"
                  required
                  value={this.state.lastname}
                  onChange={this.handleInputChange("lastname")}
                  InputProps={{ style: { borderRadius: 16 } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  Email Address <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Please enter your email."
                  fullWidth
                  size="small"
                  required
                  value={this.state.email}
                  onChange={this.handleInputChange("email")}
                  InputProps={{ style: { borderRadius: 16 } }}
                  error={this.state.email.trim().length > 0 && !this.isValidEmail(this.state.email)}
                  helperText={
                    this.state.email.trim().length > 0 && !this.isValidEmail(this.state.email)
                      ? "Please enter a valid email address."
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  Phone Number <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Please enter your phone number."
                  fullWidth
                  size="small"
                  required
                  value={this.state.phone}
                  onChange={this.handleInputChange("phone")}
                  InputProps={{ style: { borderRadius: 16 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  Company Name <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Please enter your company name."
                  fullWidth
                  size="small"
                  required
                  value={this.state.company}
                  onChange={this.handleInputChange("company")}
                  InputProps={{ style: { borderRadius: 16 } }}
                />
              </Grid>
              {/* Chain Name (required) and Stores (optional) */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  Chain Name <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Enter chain name..."
                  fullWidth
                  size="small"
                  required
                  value={this.state.chainName}
                  onChange={this.handleInputChange("chainName")}
                  InputProps={{ style: { borderRadius: 16 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  Stores
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Enter stores..."
                  fullWidth
                  size="small"
                  value={this.state.stores}
                  onChange={this.handleInputChange("stores")}
                  InputProps={{ style: { borderRadius: 16 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  Input Details?{" "}
                  <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Please specify additional information."
                  fullWidth
                  size="small"
                  required
                  value={this.state.chain}
                  onChange={this.handleInputChange("chain")}
                  InputProps={{ style: { borderRadius: 16 } }}
                />
              </Grid>
            </Grid>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px 32px" }}>
            <Button
              variant="outlined"
              onClick={() => hideUserCreationModal()}
              style={{
                borderRadius: 6,
                textTransform: "none",
                padding: "8px 18px",
                marginRight: 12,
              }}
              disabled={loading}
              className={classes.cancelBtn}
            >
              Cancel
            </Button>
            <StyledTooltip title={!hasValue ? "Fill required fields" : ""} arrow placement="top">
              <span
                style={{ display: "inline-block", cursor: !isValid ? "not-allowed" : "pointer" }}
              >
                <Button
                  variant="contained"
                  onClick={this.handleSubmit}
                  style={{ ...submitStyle, cursor: "inherit" }}
                  className={classes.submitBtn}
                  disabled={!isValid}
                >
                  {loading && (
                    <CircularProgress size={16} style={{ color: "#000", marginRight: 8 }} />
                  )}
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </span>
            </StyledTooltip>
          </div>
        </Paper>
      </Modal>
    );
  }
}

const connecter = connect(
  (state: State) => ({
    showModal: state.requests.showUserCreationModal,
    request: state.requests.anonymousRequest,
  }),
  { hideUserCreationModal, createAnonymousRequest }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default connecter(withStyles(styles)(UserCreationRequestModal));
